import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "motion/react";
import { 
  Plus, 
  Send, 
  Users, 
  Globe, 
  Calendar, 
  Edit, 
  Trash2, 
  Eye,
  Filter,
  Search,
  X
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { Badge } from "./ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Label } from "./ui/label";
import { ConfirmationDialog } from "./ui/confirmation-dialog";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import announcementService, { 
  Announcement, 
  CreateAnnouncementData, 
  Student 
} from "../services/announcement.service";

interface AdminAnnouncementsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AdminAnnouncements({ open, onOpenChange }: AdminAnnouncementsProps) {
  const { user } = useAuth();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  const [filter, setFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ open: boolean; announcementId: string | null }>({
    open: false,
    announcementId: null
  });

  // Form state
  const [formData, setFormData] = useState<CreateAnnouncementData>({
    title: '',
    content: '',
    recipients: [],
    isPublic: true,
    priority: 'normal',
    expiresAt: ''
  });
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);

  const fetchAnnouncements = useCallback(async () => {
    try {
      setLoading(true);
      const response = await announcementService.getAnnouncementsForAdmin({
        status: filter,
        limit: 50
      });
      setAnnouncements(response.data.announcements);
    } catch (error: any) {
      if (error.message?.includes('User not found')) {
        toast.error('Authentication expired. Please login again.');
        // Could trigger a logout here if needed
      } else {
        toast.error(error.message || 'Failed to fetch announcements');
      }
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const fetchStudents = useCallback(async () => {
    try {
      const response = await announcementService.getStudents();
      setStudents(response.data.students || []);
    } catch (error: any) {
      if (error.message?.includes('User not found')) {
        toast.error('Authentication expired. Please login again.');
      } else {
        toast.error(error.message || 'Failed to fetch students');
      }
      setStudents([]);
    }
  }, []);

  useEffect(() => {
    if (open && user?.role === 'admin') {
      fetchAnnouncements();
      fetchStudents();
    } else if (open && user?.role !== 'admin') {
      toast.error('Access denied. Admin privileges required.');
      onOpenChange(false);
    }
  }, [open, filter, user, fetchAnnouncements, fetchStudents, onOpenChange]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('Title and content are required');
      return;
    }

    try {
      setLoading(true);
      
      const submitData: CreateAnnouncementData = {
        ...formData,
        recipients: formData.isPublic ? [] : selectedStudents,
        expiresAt: formData.expiresAt || undefined
      };

      if (editingAnnouncement) {
        await announcementService.updateAnnouncement(editingAnnouncement._id, submitData);
        toast.success('Announcement updated successfully');
      } else {
        await announcementService.createAnnouncement(submitData);
        toast.success('Announcement created successfully');
      }

      resetForm();
      fetchAnnouncements();
    } catch (error: any) {
      toast.error(error.message || 'Failed to save announcement');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (id: string) => {
    setDeleteConfirmation({ open: true, announcementId: id });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmation.announcementId) return;

    try {
      await announcementService.deleteAnnouncement(deleteConfirmation.announcementId);
      toast.success('Announcement deleted successfully');
      fetchAnnouncements();
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete announcement');
    }
  };

  const handleEdit = (announcement: Announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      content: announcement.content,
      recipients: announcement.recipients.map(r => r._id),
      isPublic: announcement.isPublic,
      priority: announcement.priority,
      expiresAt: announcement.expiresAt ? new Date(announcement.expiresAt).toISOString().slice(0, 16) : ''
    });
    setSelectedStudents(announcement.recipients.map(r => r._id));
    setShowCreateForm(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      recipients: [],
      isPublic: true,
      priority: 'normal',
      expiresAt: ''
    });
    setSelectedStudents([]);
    setEditingAnnouncement(null);
    setShowCreateForm(false);
  };

  const handleStudentToggle = (studentId: string) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-500 text-white border-red-500';
      case 'high': return 'bg-orange-500 text-white border-orange-500';
      case 'normal': return 'bg-blue-500 text-white border-blue-500';
      case 'low': return 'bg-slate-400 text-white border-slate-400';
      default: return 'bg-slate-400 text-white border-slate-400';
    }
  };

  const getPriorityAccent = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'border-l-red-500';
      case 'high': return 'border-l-orange-500';
      case 'normal': return 'border-l-blue-500';
      case 'low': return 'border-l-slate-400';
      default: return 'border-l-slate-400';
    }
  };

  const filteredAnnouncements = announcements.filter(announcement =>
    announcement.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    announcement.content.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] w-full max-h-[90vh] flex flex-col p-0">
        <DialogHeader className="p-6 pb-4 border-b bg-white sticky top-0 z-10">
          <div className="flex items-center justify-between mb-4">
            <DialogTitle className="text-2xl font-bold text-[#0f1f3d]">
              Announcement Management
            </DialogTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
              className="h-8 w-8 p-0 hover:bg-gray-100"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="space-y-4">
            
            {/* Search Row */}
            <div className="w-full">
              <div className="relative max-w-md flex items-center">
                <Search className="absolute left-3 text-gray-400 w-4 h-4 pointer-events-none" />
                <Input
                  placeholder="Search announcements..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full bg-white border border-gray-300 focus:border-[#d4af37] focus:ring-1 focus:ring-[#d4af37]"
                />
              </div>
            </div>

            {/* Filter Row */}
            <div className="w-full">
              <Select value={filter} onValueChange={(value: any) => setFilter(value)}>
                <SelectTrigger className="w-full bg-white border border-gray-300 hover:bg-gray-50 focus:border-[#d4af37] focus:ring-1 focus:ring-[#d4af37]">
                  <Filter className="w-4 h-4 mr-2 flex-shrink-0" />
                  <SelectValue placeholder="Filter announcements..." />
                </SelectTrigger>
                <SelectContent 
                  className="bg-white border border-gray-300 shadow-lg p-1"
                  style={{ width: 'var(--radix-select-trigger-width)' }}
                  position="popper"
                  sideOffset={4}
                >
                  <SelectItem value="all" className="hover:bg-gray-100 focus:bg-gray-100 cursor-pointer data-[state=checked]:bg-[#d4af37] data-[state=checked]:text-[#0f1f3d]">
                    All Announcements
                  </SelectItem>
                  <SelectItem value="active" className="hover:bg-gray-100 focus:bg-gray-100 cursor-pointer data-[state=checked]:bg-[#d4af37] data-[state=checked]:text-[#0f1f3d]">
                    Active Only
                  </SelectItem>
                  <SelectItem value="inactive" className="hover:bg-gray-100 focus:bg-gray-100 cursor-pointer data-[state=checked]:bg-[#d4af37] data-[state=checked]:text-[#0f1f3d]">
                    Inactive Only
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Button Row */}
            <div className="w-full">
              <Button 
                onClick={() => setShowCreateForm(true)}
                className="btn-gold w-full sm:w-auto"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Announcement
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-6 space-y-6 min-w-0">

          {/* Create/Edit Form */}
          <AnimatePresence>
            {showCreateForm && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="w-full"
              >
                <Card className="border-2 shadow-lg">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-lg font-semibold text-[#0f1f3d]">
                      {editingAnnouncement ? 'Edit Announcement' : 'Create New Announcement'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="title">Title *</Label>
                          <Input
                            id="title"
                            value={formData.title}
                            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                            placeholder="Announcement title"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="priority">Priority</Label>
                          <Select 
                            value={formData.priority} 
                            onValueChange={(value: any) => setFormData(prev => ({ ...prev, priority: value }))}
                          >
                            <SelectTrigger className="w-full bg-white border border-gray-300 hover:bg-gray-50 focus:border-[#d4af37] focus:ring-1 focus:ring-[#d4af37]">
                              <SelectValue placeholder="Select priority..." />
                            </SelectTrigger>
                            <SelectContent 
                              className="bg-white border border-gray-300 shadow-lg p-1"
                              style={{ width: 'var(--radix-select-trigger-width)' }}
                              position="popper"
                              sideOffset={4}
                            >
                              <SelectItem value="low" className="hover:bg-gray-100 focus:bg-gray-100 cursor-pointer data-[state=checked]:bg-[#d4af37] data-[state=checked]:text-[#0f1f3d]">Low</SelectItem>
                              <SelectItem value="normal" className="hover:bg-gray-100 focus:bg-gray-100 cursor-pointer data-[state=checked]:bg-[#d4af37] data-[state=checked]:text-[#0f1f3d]">Normal</SelectItem>
                              <SelectItem value="high" className="hover:bg-gray-100 focus:bg-gray-100 cursor-pointer data-[state=checked]:bg-[#d4af37] data-[state=checked]:text-[#0f1f3d]">High</SelectItem>
                              <SelectItem value="urgent" className="hover:bg-gray-100 focus:bg-gray-100 cursor-pointer data-[state=checked]:bg-[#d4af37] data-[state=checked]:text-[#0f1f3d]">Urgent</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="content">Content *</Label>
                        <Textarea
                          id="content"
                          value={formData.content}
                          onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                          placeholder="Announcement content"
                          rows={4}
                          required
                        />
                      </div>

                      <div className="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg">
                        <Checkbox
                          id="isPublic"
                          checked={formData.isPublic}
                          onCheckedChange={(checked) => 
                            setFormData(prev => ({ ...prev, isPublic: !!checked }))
                          }
                        />
                        <Label htmlFor="isPublic" className="flex items-center font-medium">
                          <Globe className="w-4 h-4 ml-2" />
                          Send to all students
                        </Label>
                      </div>

                      {!formData.isPublic && (
                        <div className="space-y-3">
                          <Label className="font-medium">Select Recipients</Label>
                          <div className="border rounded-lg p-4 max-h-40 overflow-y-auto bg-white">
                            {students.length > 0 ? (
                              students.map((student) => (
                                <div key={student._id} className="flex items-center space-x-3 py-2 hover:bg-gray-50 rounded px-2">
                                  <Checkbox
                                    id={student._id}
                                    checked={selectedStudents.includes(student._id)}
                                    onCheckedChange={() => handleStudentToggle(student._id)}
                                  />
                                  <Label htmlFor={student._id} className="text-sm cursor-pointer flex-1 ml-2">
                                    {student.name} ({student.email})
                                  </Label>
                                </div>
                              ))
                            ) : (
                              <div className="text-center py-8 text-gray-500">
                                <p className="text-sm">No students available</p>
                                <p className="text-xs mt-1">Please check if students are registered in the system</p>
                              </div>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 font-medium bg-blue-50 p-2 rounded">
                            {selectedStudents.length} student(s) selected
                          </p>
                        </div>
                      )}

                      <div className="space-y-2">
                        <Label htmlFor="expiresAt">Expires At (Optional)</Label>
                        <Input
                          id="expiresAt"
                          type="datetime-local"
                          value={formData.expiresAt}
                          onChange={(e) => setFormData(prev => ({ ...prev, expiresAt: e.target.value }))}
                        />
                      </div>

                      <div className="flex gap-3 pt-4 border-t">
                        <Button 
                          type="submit" 
                          disabled={loading}
                          className="btn-gold px-6 py-2"
                        >
                          <Send className="w-4 h-4 mr-2" />
                          {editingAnnouncement ? 'Update' : 'Create'} Announcement
                        </Button>
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={resetForm}
                          className="px-6 py-2"
                        >
                          Cancel
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Announcements List */}
          <div className="space-y-4">
            {loading && !showCreateForm ? (
              <div className="flex justify-center items-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#d4af37]"></div>
              </div>
            ) : (
              <div className="grid gap-4">
                {filteredAnnouncements.map((announcement) => (
                  <motion.div
                    key={announcement._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`group relative bg-white rounded-xl border-l-4 ${getPriorityAccent(announcement.priority)} shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden`}
                  >
                    {/* Card Header */}
                    <div className="p-6 pb-5">
                      <div className="flex items-start justify-between gap-4">
                        {/* Left: Title & Priority */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className={`${getPriorityColor(announcement.priority)} text-xs font-medium px-2.5 py-0.5 rounded-full shadow-sm`}>
                              {announcement.priority.charAt(0).toUpperCase() + announcement.priority.slice(1)}
                            </Badge>
                            {!announcement.isActive && (
                              <Badge variant="secondary" className="bg-gray-200 text-gray-600 text-xs">
                                Inactive
                              </Badge>
                            )}
                          </div>
                          <h3 className="font-bold text-[#0f1f3d] text-lg leading-tight mb-2 truncate">
                            {announcement.title}
                          </h3>
                          <p className="text-gray-600 text-sm line-clamp-2 leading-relaxed">
                            {announcement.content}
                          </p>
                        </div>

                        {/* Right: Action Buttons */}
                        <div className="flex items-center gap-1.5 opacity-70 group-hover:opacity-100 transition-opacity">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEdit(announcement)}
                            className="h-9 w-9 p-0 rounded-lg hover:bg-blue-50 hover:text-blue-600 transition-colors"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteClick(announcement._id)}
                            className="h-9 w-9 p-0 rounded-lg hover:bg-red-50 text-gray-500 hover:text-red-600 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Card Footer: Meta Info */}
                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                      <div className="flex flex-wrap items-center gap-4 text-xs">
                        <div className="flex items-center gap-1.5 text-gray-600">
                          {announcement.isPublic ? (
                            <>
                              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100">
                                <Globe className="w-3.5 h-3.5 text-emerald-600" />
                              </div>
                              <span className="font-medium">All Students</span>
                            </>
                          ) : (
                            <>
                              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-violet-100">
                                <Users className="w-3.5 h-3.5 text-violet-600" />
                              </div>
                              <span className="font-medium">{announcement.recipients.length} Recipients</span>
                            </>
                          )}
                        </div>
                        
                        {announcement.expiresAt && (
                          <div className="flex items-center gap-1.5 text-gray-600">
                            <div className="flex items-center justify-center w-6 h-6 rounded-full bg-amber-100">
                              <Calendar className="w-3.5 h-3.5 text-amber-600" />
                            </div>
                            <span>Expires {new Date(announcement.expiresAt).toLocaleDateString()}</span>
                          </div>
                        )}
                        
                        <div className="flex items-center gap-1.5 text-gray-500 ml-auto">
                          <span>Created {new Date(announcement.createdAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Read Status Section */}
                    {announcement.readCount !== undefined && (
                      <div className="px-6 py-5 bg-white border-t border-gray-100">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <div className="flex items-center justify-center w-7 h-7 rounded-full bg-[#d4af37]/10">
                              <Eye className="w-4 h-4 text-[#d4af37]" />
                            </div>
                            <span className="font-semibold text-gray-700 text-sm">Read Status</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <span className="font-bold text-[#0f1f3d] text-lg">
                                {announcement.readCount}
                              </span>
                              <span className="text-gray-400 mx-1">/</span>
                              <span className="text-gray-500">
                                {announcement.totalRecipients}
                              </span>
                            </div>
                            <div className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                              (announcement.readPercentage || 0) >= 75 
                                ? 'bg-emerald-100 text-emerald-700' 
                                : (announcement.readPercentage || 0) >= 50 
                                  ? 'bg-amber-100 text-amber-700'
                                  : (announcement.readPercentage || 0) >= 25
                                    ? 'bg-orange-100 text-orange-700'
                                    : 'bg-red-100 text-red-700'
                            }`}>
                              {announcement.readPercentage}%
                            </div>
                          </div>
                        </div>
                        <div className="relative w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
                          <motion.div 
                            initial={{ width: 0 }}
                            animate={{ width: `${announcement.readPercentage}%` }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                            className={`absolute left-0 top-0 h-full rounded-full ${
                              (announcement.readPercentage || 0) >= 75 
                                ? 'bg-gradient-to-r from-emerald-400 to-emerald-500' 
                                : (announcement.readPercentage || 0) >= 50 
                                  ? 'bg-gradient-to-r from-amber-400 to-amber-500'
                                  : (announcement.readPercentage || 0) >= 25
                                    ? 'bg-gradient-to-r from-orange-400 to-orange-500'
                                    : 'bg-gradient-to-r from-red-400 to-red-500'
                            }`}
                          />
                        </div>
                      </div>
                    )}
                  </motion.div>
                ))}
                
                {filteredAnnouncements.length === 0 && !loading && (
                  <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                    <div className="space-y-2">
                      <p className="text-lg font-medium">
                        {searchTerm ? 'No announcements found matching your search.' : 'No announcements yet.'}
                      </p>
                      {!searchTerm && (
                        <p className="text-sm">
                          Create your first announcement to get started.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
      
      <ConfirmationDialog
        open={deleteConfirmation.open}
        onOpenChange={(open) => setDeleteConfirmation({ open, announcementId: null })}
        title="Delete Announcement"
        description="Are you sure you want to delete this announcement? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        variant="destructive"
      />
    </Dialog>
  );
}
